// Client_Directory_SubmitData.js — Redesigned 3-Step Wizard
import {
  Box, Button, Divider, Paper, TextField, Typography, Autocomplete
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FiBox, FiCheck, FiChevronLeft, FiChevronRight,
  FiColumns, FiMapPin, FiSearch, FiSend
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { MainContainer } from "../../components/container/MainContainer";
import TextBox from "../../components/input/TextBox";
import ResizableTextBox from "../../components/input/ResizableTextBox";
import SelectBox from "../../components/input/SelectBox";
import { Emission } from "../../db_access/emissions";
import { Partnership } from "../../db_access/partnership";
import { Product } from "../../db_access/product";
import { Project } from "../../db_access/project";
import { Structure } from "../../db_access/structure";
import { Supplier_Reports } from "../../db_access/supplier_reports";
import "../../style/pages-style/Engagement/engagement.css";
import { scope1Categories, scope2Categories, scope3CategoriesForSupplyChain } from "../Organization/ScopeCategory";
import routing from "../../routing";
import { getOrgTokens, ORG_PALETTE } from "../../theme/style/organizationPalette";
import { useTheme } from "@mui/material";

/* ─── constants ──────────────────────────────────────────────────── */
const STEPS = ["Choose data level", "Configure report", "Select client"];

const LEVELS = [
  {
    value: "product_level", label: "Product level", Icon: FiBox,
    desc: "Share a specific product's carbon footprint",
  },
  {
    value: "project_level", label: "Project level", Icon: FiColumns,
    desc: "Share a construction or design project's data",
  },
  {
    value: "facility_level", label: "Facility level", Icon: FiMapPin,
    desc: "Share a facility or building's emission data",
  },
];

const GREEN = "#16a34a";
const GREEN_DARK = "#15803d";
const GREEN_LIGHT = "#f0fdf4";
const GREEN_RING = "rgba(22,163,74,0.10)";

/* ─── component ──────────────────────────────────────────────────── */
const Client_Directory_SubmitData = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);

  const defaultData = useMemo(() => ({
    year: "", quantity: "", functional_unit: "", other_unit: "",
    gwp_per_unit: "", cost_per_unit: "", gwp: 0, cost: 0, name: "",
  }), []);

  const [data, setData] = useState(defaultData);
  const [level, setLevel] = useState(null);
  const [options, setOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [scope, setScope] = useState(null);
  const [notes, setNotes] = useState("");
  const [partners, setPartners] = useState([]);
  const [receiver, setReceiver] = useState(null);
  const [emissions, setEmissions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const setDataField = (field) => (e) => setData((p) => ({ ...p, [field]: e?.target?.value }));
  const isDataLevel = useCallback((v) => level === v, [level]);

  const scope_all = useMemo(() => ({ label: "All scopes", value: "scope_all", filter: () => true }), []);
  const scope_1 = useMemo(() => ({ label: "Scope 1", value: "scope_1", filter: (i) => !i.is_avoided && scope1Categories.includes(i.category) }), []);
  const scope_2 = useMemo(() => ({ label: "Scope 2", value: "scope_2", filter: (i) => !i.is_avoided && scope2Categories.includes(i.category) }), []);
  const scope_3 = useMemo(() => ({ label: "Scope 3", value: "scope_3", filter: (i) => !i.is_avoided && scope3CategoriesForSupplyChain.includes(i.category) }), []);
  const scopes = useMemo(() => [scope_all, scope_1, scope_2, scope_3], [scope_all, scope_1, scope_2, scope_3]);

  useEffect(() => {
    setSelectedOption(null); setOptions([]);
    const mapper = (name_key) => (d) => ({
      ...d,
      name: d[name_key] || d.name || "Item",
      label: (d[name_key] || d.name || "Item") + " " + d.year,
      value: d.id,
      gwp_per_unit: d.gwp || 0, cost_per_unit: d.cost || 0,
      gwp: (d.gwp || 0) * (d.quantity || 1),
      cost: (d.cost || 0) * (d.quantity || 1),
      functional_unit: d.functional_unit || "", other_unit: d.other_unit || "",
    });
    if (level === "product_level") Product.get_mine().then(r => r.json()).then(d => setOptions(d.map(mapper("product"))));
    else if (level === "project_level") Project.get_mine().then(r => r.json()).then(d => setOptions(d.map(mapper("design_installation"))));
    else if (level === "facility_level") Structure.get_mine().then(r => r.json()).then(d => setOptions(d.map(mapper("name"))));
  }, [level]);

  useEffect(() => {
    if (!selectedOption) { setData(defaultData); setEmissions([]); return; }
    setData(selectedOption);
    if (isDataLevel("facility_level")) {
      Emission.getEmissionsByStructureId(selectedOption.id).then(r => r.json()).then(setEmissions);
    }
  }, [selectedOption, defaultData, isDataLevel]);

  useEffect(() => {
    if (scopes.map(s => s.value).includes(scope)) {
      const f = scopes.find(s => s.value === scope).filter;
      const gwp_per_unit = emissions.filter(f).reduce((sum, e) => sum + (e.gwp || 0), 0);
      setData(prev => ({ ...prev, gwp_per_unit }));
    }
  }, [scope, emissions, scopes]);

  useEffect(() => {
    setData(p => ({
      ...p,
      gwp: (p.gwp_per_unit || 0) * (p.quantity || 0),
      cost: (p.cost_per_unit || 0) * (p.quantity || 0),
    }));
  }, [data.gwp_per_unit, data.cost_per_unit, data.quantity]);

  useEffect(() => {
    Partnership.get_my_partners().then(r => r.json()).then(d => {
      if (d) setPartners(d.map(p => ({ label: p.name, value: p.id })));
    });
  }, []);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await Supplier_Reports.new({
        ...data, level, scope: scope === "scope_all" ? null : scope,
        name: data.name, notes, receiver_id: receiver?.value,
      });
      setSubmitted(true);
    } catch (e) { console.error(e); }
    setSubmitting(false);
  };

  const canAdvance = [
    () => !!level,
    () => !!selectedOption && !!data.year && !!data.quantity,
    () => !!receiver,
  ];

  /* ── shared sx helpers ── */
  const inputSx = {
    "& .MuiOutlinedInput-root": {
      background: isDark ? tok.bg.main : "#f9fafb",
      borderRadius: ORG_PALETTE.radius.card,
      fontSize: 14,
      "& fieldset": { border: `0.5px solid ${tok.border.card}` },
      "&:hover fieldset": { borderColor: GREEN },
      "&.Mui-focused fieldset": { borderColor: GREEN, borderWidth: "1px" },
    },
    "& .MuiInputBase-root": { fontFamily: ORG_PALETTE.font.family, fontSize: 14 },
  };

  const fieldLabelSx = {
    fontFamily: ORG_PALETTE.font.family,
    fontSize: 13,
    fontWeight: 400, // Standard weight
    color: tok.text.primary,
    mb: 1,
    textAlign: "left",
  };

  /* ── Success screen ── */
  if (submitted) {
    return (
      <MainContainer>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
          <Paper elevation={0} sx={{ p: 6, borderRadius: ORG_PALETTE.radius.card, border: `0.5px solid ${tok.border.card}`, textAlign: "center", maxWidth: 440, width: "100%", bgcolor: tok.bg.card, boxShadow: tok.shadow.card }}>
            <Box sx={{
              width: 72, height: 72, borderRadius: "50%",
              background: "#dcfce7", color: GREEN,
              display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 3
            }}>
              <FiCheck size={32} />
            </Box>
            <Typography sx={{ fontFamily: ORG_PALETTE.font.family, fontSize: 22, fontWeight: 500, color: tok.text.primary, mb: 1 }}>
              Report sent successfully!
            </Typography>
            <Typography sx={{ fontFamily: ORG_PALETTE.font.family, fontSize: 14, color: tok.text.chartSub, mb: 4, lineHeight: 1.7 }}>
              Your carbon data has been shared with <strong>{receiver?.label}</strong>. It is now awaiting their review.
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Button
                variant="contained"
                onClick={() => { setSubmitted(false); setLevel(null); setData(defaultData); setReceiver(null); setNotes(""); }}
                sx={{
                  backgroundColor: GREEN, "&:hover": { backgroundColor: GREEN_DARK },
                  fontFamily: ORG_PALETTE.font.family, fontSize: 13, fontWeight: 500,
                  textTransform: "none", borderRadius: ORG_PALETTE.radius.card, py: 1.4,
                }}>
                Submit another report
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate(routing.engagement.client_directory.my_submissions)}
                sx={{
                  color: tok.text.secondary, borderColor: tok.border.card,
                  "&:hover": { borderColor: GREEN, color: GREEN, background: GREEN_LIGHT },
                  fontFamily: ORG_PALETTE.font.family, fontSize: 13, fontWeight: 500,
                  textTransform: "none", borderRadius: ORG_PALETTE.radius.card, py: 1.4,
                }}>
                View my submissions
              </Button>
            </Box>
          </Paper>
        </Box>
      </MainContainer>
    );
  }

  /* ── Removed traditional Stepper and NavFooter for a vertically stacked flow ── */

  return (
    <MainContainer>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pb: 4 }}>

        {/* ── Step 1: Choose data level ── */}
        <Paper elevation={0} sx={{
          border: `0.5px solid ${tok.border.card}`, background: tok.bg.card,
          borderRadius: ORG_PALETTE.radius.card, overflow: "hidden",
        }}>
          <Box sx={{ px: 4, py: 2.5, borderBottom: `0.5px solid ${tok.border.divider}`, background: isDark ? tok.bg.main : "#f8fafc", display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ width: 24, height: 24, borderRadius: "50%", background: GREEN, color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 600 }}>1</Box>
            <Typography sx={{ fontFamily: ORG_PALETTE.font.family, fontSize: 16, fontWeight: 600, color: tok.text.primary }}>
              Choose Data Level
            </Typography>
          </Box>
          <Box sx={{ p: 2, display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 1.5 }}>
            {LEVELS.map(({ value, label, Icon, desc }) => (
              <Box
                key={value}
                onClick={() => setLevel(value)}
                sx={{
                  display: "flex", flexDirection: "column", alignItems: "center",
                  justifyContent: "center", textAlign: "center",
                  padding: "16px 12px",
                  border: level === value ? `1.5px solid ${GREEN}` : `1px solid ${tok.border.card}`,
                  borderRadius: "12px", cursor: "pointer",
                  background: level === value ? GREEN_LIGHT : tok.bg.card,
                  boxShadow: level === value ? `0 0 0 3px ${GREEN_RING}` : "none",
                  transition: "all 0.18s",
                  "&:hover": {
                    borderColor: GREEN,
                    background: level === value ? GREEN_LIGHT : (isDark ? tok.bg.main : "#f9fafb"),
                  },
                }}>
                <Box sx={{
                  width: 36, height: 36, borderRadius: "50%", mb: 1,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  background: level === value ? "#dcfce7" : (isDark ? tok.bg.main : "#f3f4f6"),
                  transition: "background 0.18s",
                }}>
                  <Icon size={16} color={level === value ? GREEN : tok.text.secondary} />
                </Box>
                <Typography sx={{ fontFamily: ORG_PALETTE.font.family, fontSize: 12.5, fontWeight: 600, color: level === value ? GREEN_DARK : tok.text.primary, mb: 0.25 }}>
                  {label}
                </Typography>
                <Typography sx={{ fontFamily: ORG_PALETTE.font.family, fontSize: 10, color: tok.text.chartSub, lineHeight: 1.4 }}>
                  {desc}
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>

        {/* ── Step 2: Configure report ── */}
        {level && (
          <Paper elevation={0} sx={{
            border: `0.5px solid ${tok.border.card}`, background: tok.bg.card,
            borderRadius: ORG_PALETTE.radius.card, overflow: "hidden",
            animation: "eng-fadeInUp 0.4s ease both"
          }}>
            <Box sx={{ px: 4, py: 2.5, borderBottom: `0.5px solid ${tok.border.divider}`, background: isDark ? tok.bg.main : "#f8fafc", display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={{ width: 24, height: 24, borderRadius: "50%", background: GREEN, color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 600 }}>2</Box>
              <Typography sx={{ fontFamily: ORG_PALETTE.font.family, fontSize: 16, fontWeight: 600, color: tok.text.primary }}>
                Configure Your Report
              </Typography>
            </Box>
            <Box sx={{ p: 4, display: "flex", flexDirection: "column", gap: 3.5 }}>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3 }}>
                {/* Item picker */}
                <Box>
                  <Typography sx={fieldLabelSx}>Select Item ({LEVELS.find(l => l.value === level)?.label})</Typography>
                  {options.length > 0 ? (
                    <SelectBox placeholder="Choose an item…" options={options} freeSolo={false} onChange={(_, v) => setSelectedOption(v)} value={selectedOption} />
                  ) : (
                    <Box sx={{ p: 2, border: `1px dashed ${tok.border.card}`, borderRadius: "10px", textAlign: "center", mt: 1 }}>
                      <Typography sx={{ fontFamily: ORG_PALETTE.font.family, fontSize: 13, color: tok.text.secondary }}>
                        No items found. Please create one in the {LEVELS.find(l => l.value === level)?.label} section.
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Scope filter (facility only) */}
                {level === "facility_level" && (
                  <Box>
                    <Typography sx={fieldLabelSx}>Scope Filter</Typography>
                    <SelectBox placeholder="Select scope" options={scopes} freeSolo={false} onChange={(_, v) => setScope(v?.value)} value={scope} />
                  </Box>
                )}
              </Box>

              <Divider sx={{ borderColor: tok.border.divider }} />

              {/* 6-column fields grid */}
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)", md: "repeat(6, 1fr)" }, gap: 2 }}>
                {[
                  { label: "Year", field: "year" },
                  { label: "Quantity", field: "quantity" },
                  { label: "Functional unit", field: "functional_unit" },
                  { label: "Other unit", field: "other_unit" },
                  { label: "GWP per unit", field: "gwp_per_unit" },
                  { label: "Cost per unit", field: "cost_per_unit" },
                ].map(f => (
                  <Box key={f.field}>
                    <Typography sx={fieldLabelSx}>{f.label}</Typography>
                    <TextBox placeholder={f.label} value={data?.[f.field]} onChange={setDataField(f.field)} />
                  </Box>
                ))}
              </Box>

              {/* Totals Section - Side-by-Side Row */}
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2.5 }}>
                {[
                  { label: "Total GWP", field: "gwp", unit: "kg CO₂-eq", color: GREEN },
                  { label: "Total Cost", field: "cost", unit: "₹", color: tok.text.primary },
                ].map(f => (
                  <Box key={f.field} sx={{ px: 2.5, py: 2.2, display: "flex", alignItems: "center", justifyContent: "space-between", border: `1px solid ${tok.border.divider}`, borderRadius: "10px", background: isDark ? tok.bg.main : "#f8fafc" }}>
                    <Typography sx={{ fontFamily: ORG_PALETTE.font.family, fontSize: 13, fontWeight: 500, color: tok.text.chartSub }}>
                      {f.label}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
                      <Typography sx={{ fontFamily: ORG_PALETTE.font.family, fontSize: 20, fontWeight: 700, color: f.color }}>
                        {Math.round(data?.[f.field] || 0).toLocaleString()}
                      </Typography>
                      <Typography sx={{ fontFamily: ORG_PALETTE.font.family, fontSize: 11, fontWeight: 500, color: tok.text.chartSub }}>
                        {f.unit}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>

              {/* Notes Section - Full Width Row Below */}
              <Box sx={{ display: "flex", flexDirection: "column", mt: 2.5 }}>
                <Typography sx={{ ...fieldLabelSx, mb: 1 }}>Additional Notes</Typography>
                <ResizableTextBox
                  placeholder="Optional notes for the recipient…"
                  onChange={e => setNotes(e.target.value)}
                  minRows={3}
                  sx={{
                    "& textarea": {
                      background: isDark ? tok.bg.main : "#f8fafc",
                      borderRadius: "10px",
                      border: `1px solid ${tok.border.divider}`,
                      padding: "12px 16px",
                      fontFamily: ORG_PALETTE.font.family,
                      fontSize: 13,
                    }
                  }}
                />
              </Box>
            </Box>
          </Paper>
        )}

        {/* ── Step 3: Select client ── */}
        {level && canAdvance[1]() && (
          <Paper elevation={0} sx={{
            border: `0.5px solid ${tok.border.card}`, background: tok.bg.card,
            borderRadius: ORG_PALETTE.radius.card, overflow: "hidden",
            animation: "eng-fadeInUp 0.4s ease both"
          }}>
            <Box sx={{ px: 4, py: 2.5, borderBottom: `0.5px solid ${tok.border.divider}`, background: isDark ? tok.bg.main : "#f8fafc", display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={{ width: 24, height: 24, borderRadius: "50%", background: GREEN, color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 600 }}>3</Box>
              <Typography sx={{ fontFamily: ORG_PALETTE.font.family, fontSize: 16, fontWeight: 600, color: tok.text.primary }}>
                Select Client & Submit
              </Typography>
            </Box>
            <Box sx={{ p: 4, display: "flex", flexDirection: "column", gap: 4 }}>

              {/* Client search */}
              <Box>
                <Typography sx={{ ...fieldLabelSx, mb: 1 }}>Select Partner</Typography>
                <Autocomplete
                  disablePortal options={partners} value={receiver} onChange={(_, v) => setReceiver(v)}
                  renderInput={(params) => (
                    <TextField
                      {...params} placeholder="Search your partners…"
                      InputProps={{ ...params.InputProps, startAdornment: (<Box sx={{ ml: 0.5, mr: -0.5, color: tok.text.secondary, display: "flex" }}><FiSearch size={15} /></Box>) }}
                      sx={inputSx}
                    />
                  )}
                  PaperComponent={(props) => (
                    <Paper {...props} sx={{ fontFamily: ORG_PALETTE.font.family, borderRadius: "10px", mt: 0.5, border: `1px solid ${tok.border.card}`, boxShadow: tok.shadow.card }} />
                  )}
                />

                {/* Selected partner chip */}
                {receiver && (
                  <Box sx={{ mt: 2, px: 2.5, py: 2, border: `1.5px solid ${GREEN}`, borderRadius: "12px", background: GREEN_LIGHT, display: "flex", alignItems: "center", gap: 2 }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: "50%", background: "#dcfce7", color: GREEN, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 600, flexShrink: 0 }}>
                      {receiver.label?.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2)}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontFamily: ORG_PALETTE.font.family, fontSize: 15, fontWeight: 600, color: "#166534", mb: 0.25 }}>
                        {receiver.label}
                      </Typography>
                      <Typography sx={{ fontFamily: ORG_PALETTE.font.family, fontSize: 12, color: "#4ade80", fontWeight: 500 }}>
                        Ready to receive report
                      </Typography>
                    </Box>
                    <Box sx={{ width: 24, height: 24, borderRadius: "50%", background: GREEN, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <FiCheck size={14} color="white" />
                    </Box>
                  </Box>
                )}
                {partners.length === 0 && (
                  <Typography sx={{ fontFamily: ORG_PALETTE.font.family, fontSize: 12, color: "#b45309", mt: 1 }}>
                    No active client partners found yet.
                  </Typography>
                )}
              </Box>

              {/* Summary card */}
              <Box sx={{ border: `1px dashed ${tok.border.divider}`, borderRadius: "12px", overflow: "hidden" }}>
                <Box sx={{ px: 3, py: 1.5, background: isDark ? tok.bg.main : "#f8fafc", borderBottom: `1px dashed ${tok.border.divider}` }}>
                  <Typography sx={{ fontFamily: ORG_PALETTE.font.family, fontSize: 12, fontWeight: 600, letterSpacing: "0.5px", textTransform: "uppercase", color: tok.text.secondary }}>
                    Report Summary
                  </Typography>
                </Box>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(3, minmax(0,1fr))", md: "repeat(6, minmax(0,1fr))" }, background: tok.bg.card }}>
                  {[
                    { label: "Level", val: LEVELS.find(l => l.value === level)?.label },
                    { label: "Item", val: selectedOption?.name || selectedOption?.label },
                    { label: "Year", val: data.year },
                    { label: "Quantity", val: data.quantity },
                    { label: "GWP", val: `${Math.round(data.gwp || 0).toLocaleString()} kg CO₂-eq`, green: true },
                    { label: "Cost", val: `₹ ${Math.round(data.cost || 0).toLocaleString()}` },
                  ].map((f, i, arr) => (
                    <Box key={f.label} sx={{ px: 3, py: 2, borderRight: i < arr.length - 1 ? `1px dashed ${tok.border.divider}` : "none" }}>
                      <Typography sx={{ fontFamily: ORG_PALETTE.font.family, fontSize: 10, fontWeight: 600, letterSpacing: "0.5px", textTransform: "uppercase", color: tok.text.chartSub, mb: 0.5 }}>
                        {f.label}
                      </Typography>
                      <Typography sx={{ fontFamily: ORG_PALETTE.font.family, fontSize: 13, fontWeight: 500, color: f.green ? GREEN : tok.text.primary, lineHeight: 1.4 }}>
                        {f.val || "—"}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Submit Area */}
              <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 1 }}>
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  endIcon={<FiSend size={15} />}
                  disabled={!receiver || submitting}
                  sx={{
                    backgroundColor: GREEN, "&:hover": { backgroundColor: GREEN_DARK },
                    "&:disabled": { backgroundColor: isDark ? tok.bg.main : "#e2e8f0", color: tok.text.secondary },
                    fontFamily: ORG_PALETTE.font.family, fontSize: 14, fontWeight: 600,
                    textTransform: "none", borderRadius: "10px", px: 4, py: 1.5,
                    boxShadow: (receiver && !submitting) ? `0 6px 16px rgba(22, 163, 74, 0.25)` : "none",
                  }}>
                  {submitting ? "Submitting Report…" : "Submit Report"}
                </Button>
              </Box>

            </Box>
          </Paper>
        )}

      </Box>
    </MainContainer>
  );
};

export default Client_Directory_SubmitData;